/**
 * Pilotage de la position affichée : accrochage, extrapolation et lissage.
 *
 * Portage de la boucle caméra de `MapManager.kt`. Le GPS fournit un relevé par
 * seconde au mieux ; afficher ces relevés bruts donnerait un curseur qui
 * saute. Entre deux relevés, la position est **extrapolée** à la vitesse
 * mesurée, puis lissée vers cette cible à chaque image.
 *
 * Trois modes d'extrapolation, selon ce à quoi on est accroché :
 *
 *  - **sur une trace** : avance le long de la géométrie, sans plafond — on ne
 *    peut pas quitter la trace par construction ;
 *  - **sur une route** : extrapolation rectiligne, plafonnée à 1,5 s — au-delà,
 *    l'hypothèse « on continue tout droit » devient hasardeuse ;
 *  - **libre** : même plafond, depuis le relevé brut.
 */

import { alignBearing, haversine, normalizeBearingDelta, METERS_PER_DEGREE_LAT } from './geo'
import { BEARING_SPEED_THRESHOLD_MS, type LocationSample } from './location'
import {
  SnapToRoad,
  positionBearingAtDistance,
  type RoadQuery,
  type SnapInfo,
} from './snapToRoad'
import type { GpxTrack } from './track'

/** Lissage de la position, du zoom et de l'inclinaison, par image. */
export const POSITION_ALPHA = 0.15

/**
 * Lissage du cap, par image.
 *
 * Volontairement très faible : la rotation ne doit jamais converger avant le
 * relevé suivant, sinon le mouvement paraît saccadé plutôt que continu.
 */
export const BEARING_ALPHA = 0.025

/** Durée maximale d'extrapolation rectiligne, en secondes. */
export const MAX_DEAD_RECKON_SECONDS = 1.5

/**
 * Rayon de gel de l'accrochage à l'arrêt, en mètres.
 *
 * À l'arrêt, la dérive du GPS finit par franchir le seuil de décrochage et
 * fait disparaître les panneaux alors qu'on n'a pas bougé. Tant qu'on reste
 * dans ce rayon et sous la vitesse de rotation, l'accrochage est conservé.
 */
export const STANDSTILL_HOLD_RADIUS_M = 50

/** Durée d'une image à 60 Hz, en secondes. */
const FRAME_SECONDS = 0.016

/**
 * Écarts en deçà desquels l'affichage est réputé arrivé sur sa cible.
 *
 * Repris de `MapManager.kt`. Un millionième de degré vaut environ onze
 * centimètres, un demi-degré de cap est invisible à l'écran : sous ces seuils,
 * repositionner la caméra ne change rien de perceptible. Ils ne décident donc
 * que de la mise en veille de la boucle, jamais du rendu.
 */
const POSITION_CONVERGED_DEG = 0.000001
const BEARING_CONVERGED_DEG = 0.5

export interface NavigationState {
  /** Position à afficher, après extrapolation et lissage. */
  readonly lat: number
  readonly lon: number
  /** Cap lissé, en degrés. */
  readonly bearing: number
  /** Vitesse courante, en mètres par seconde. */
  readonly speedMs: number
  /** Trace accrochée, `null` si l'on suit la route ou rien. */
  readonly snappedTrack: GpxTrack | null
  /** Abscisse curviligne sur la trace accrochée, en mètres. */
  readonly curvilinearDistance: number
  readonly isTrackSnap: boolean
  /**
   * L'affichage est arrivé sur sa cible et rien ne bouge plus.
   *
   * Ne dit rien de la validité de la position : sert à la seule mise en veille
   * de la boucle caméra.
   */
  readonly converged: boolean
}

export class NavigationController {
  readonly #snap: SnapToRoad

  // Cible issue du dernier relevé GPS.
  #targetLat = 0
  #targetLon = 0
  #targetBearing = 0
  #hasFix = false

  /**
   * La position affichée a été posée avant le premier relevé.
   *
   * Elle sert de point de départ au glissement vers la position réelle, au
   * lieu d'y sauter. Sans ce drapeau, le premier relevé écraserait la valeur
   * posée et le glissement n'aurait pas lieu.
   */
  #seeded = false

  // Position affichée, qui converge vers la cible.
  #currentLat = 0
  #currentLon = 0
  #currentBearing = 0

  #speedMs = 0
  #usingGpsBearing = false

  /** Sans relevé, rien ne converge nulle part : la boucle peut dormir. */
  #converged = true

  #snapInfo: SnapInfo | null = null
  #curvilinearDistance = -1
  #curvilinearBearing = 0

  /** Distance déjà extrapolée depuis le dernier relevé, en mètres. */
  #deadReckonMeters = 0

  /** Dernière position ayant déclenché un recalcul d'accrochage. */
  #lastSnapAt: { lat: number; lon: number } | null = null

  /**
   * Indice de progression enregistré pour une trace, en kilomètres bruts.
   *
   * Fourni de l'extérieur : la persistance ne regarde pas ce module, mais sans
   * cet indice le premier accrochage d'une session repart sans repère et peut
   * se verrouiller sur la mauvaise itération d'une boucle.
   */
  readonly #progressionHint: ((trackId: string) => number | null) | null

  constructor(
    getVisibleTracks: () => readonly GpxTrack[],
    roadQuery: RoadQuery | null = null,
    progressionHint: ((trackId: string) => number | null) | null = null,
  ) {
    this.#snap = new SnapToRoad(getVisibleTracks, roadQuery)
    this.#progressionHint = progressionHint
  }

  get hasFix(): boolean {
    return this.#hasFix
  }

  get snapInfo(): SnapInfo | null {
    return this.#snapInfo
  }

  get curvilinearDistance(): number {
    return this.#curvilinearDistance
  }

  /** Trace actuellement accrochée, s'il y en a une. */
  get snappedTrack(): GpxTrack | null {
    return this.#snapInfo?.isTrackSnap === true ? this.#snapInfo.track : null
  }

  releaseTrack(trackId: string): void {
    this.#snap.releaseTrack(trackId)
    if (this.#snapInfo?.track?.id === trackId) {
      this.#snapInfo = null
      this.#curvilinearDistance = -1
    }
  }

  reset(): void {
    this.#snap.reset()
    this.#snapInfo = null
    this.#curvilinearDistance = -1
    this.#hasFix = false
    this.#seeded = false
    this.#lastSnapAt = null
  }

  /**
   * Pose la position affichée avant qu'un relevé n'arrive.
   *
   * Sert à démarrer le suivi depuis ce que l'utilisateur a déjà sous les yeux :
   * la position réelle est ensuite rejointe par glissement, au même rythme que
   * le reste, plutôt que par un saut sec.
   *
   * Sans effet une fois la position acquise : l'affichage suit alors le GPS.
   */
  seedDisplayPosition(lat: number, lon: number, bearing: number): void {
    if (this.#hasFix) return

    this.#currentLat = lat
    this.#currentLon = lon
    this.#currentBearing = bearing
    this.#seeded = true
  }

  /**
   * Reprend le cap affiché là où la caméra se trouve.
   *
   * Appelé à chaque (re)prise du suivi, contrairement à
   * `seedDisplayPosition` : la boucle impose désormais son cap à la carte, et
   * sans cette reprise elle y sauterait depuis la valeur qu'elle avait gardée
   * — celle d'avant le geste de l'utilisateur, ou le cap du tout premier
   * relevé. Le lissage rejoint ensuite le cap réel sans à-coup.
   */
  seedDisplayBearing(bearing: number): void {
    this.#currentBearing = bearing
  }

  /** Repositionne la progression après une correction manuelle. */
  setCurvilinearDistance(distance: number): void {
    this.#curvilinearDistance = distance
  }

  /**
   * Intègre un nouveau relevé GPS.
   *
   * Le cap n'est retenu qu'au-dessus du seuil de vitesse : à l'arrêt, le cap
   * GPS tourne au gré du bruit et ferait pivoter la carte sans raison.
   */
  onLocation(sample: LocationSample): void {
    this.#targetLat = sample.lat
    this.#targetLon = sample.lon
    this.#speedMs = sample.speedMs ?? 0

    if (!this.#hasFix) {
      // Premier relevé : la position affichée est posée directement, sans
      // quoi la carte glisserait depuis le centre par défaut. À moins qu'un
      // point de départ n'ait été posé exprès, auquel cas c'est justement ce
      // glissement que l'on veut.
      if (!this.#seeded) {
        this.#currentLat = sample.lat
        this.#currentLon = sample.lon
        this.#currentBearing = sample.bearing ?? 0
      }
      this.#hasFix = true
    }

    if (sample.bearing !== null && this.#speedMs > BEARING_SPEED_THRESHOLD_MS) {
      this.#targetBearing = sample.bearing
      this.#usingGpsBearing = true
    } else {
      this.#usingGpsBearing = false
    }

    // L'accrochage est recalculé après la mise à jour du cap, pour que
    // l'alignement du segment se fasse sur une valeur fraîche.
    this.#recomputeSnapIfNeeded()
  }

  /**
   * Recalcule l'accrochage, sauf à l'arrêt sur une trace.
   *
   * Le gel évite que la dérive du GPS à l'arrêt ne fasse perdre la trace,
   * ce qui ferait disparaître l'indicateur kilométrique et le roadbook alors
   * que l'utilisateur n'a pas bougé.
   */
  #recomputeSnapIfNeeded(): void {
    const unchanged =
      this.#lastSnapAt !== null &&
      this.#lastSnapAt.lat === this.#targetLat &&
      this.#lastSnapAt.lon === this.#targetLon

    if (unchanged) return

    this.#lastSnapAt = { lat: this.#targetLat, lon: this.#targetLon }
    this.#deadReckonMeters = 0

    const holdSnap =
      this.#speedMs <= BEARING_SPEED_THRESHOLD_MS &&
      this.#snapInfo?.isTrackSnap === true &&
      haversine(
        this.#snapInfo.lat,
        this.#snapInfo.lon,
        this.#targetLat,
        this.#targetLon,
      ) < STANDSTILL_HOLD_RADIUS_M

    if (holdSnap) return

    let info = this.#snap.computeSnappedPosition(
      this.#targetLat,
      this.#targetLon,
      this.#curvilinearDistance,
    )

    // Premier accrochage de la session : on ne sait à quelle trace demander son
    // indice de progression qu'une fois celle-ci identifiée, d'où ce second
    // calcul. Il n'a lieu qu'une fois — dès le relevé suivant, la progression
    // courante fait office de repère.
    if (
      info !== null &&
      info.isTrackSnap &&
      info.track !== null &&
      this.#curvilinearDistance < 0
    ) {
      const hintKm = this.#progressionHint?.(info.track.id) ?? null
      if (hintKm !== null && hintKm >= 0) {
        info =
          this.#snap.computeSnappedPosition(
            this.#targetLat,
            this.#targetLon,
            hintKm * 1000,
          ) ?? info
      }
    }

    this.#snapInfo = info

    if (info !== null) {
      this.#curvilinearDistance = info.curvilinearDistance
      this.#curvilinearBearing = this.#alignWithGps(info.segmentBearing)
    }
  }

  /**
   * Aligne un cap de segment sur le sens de déplacement.
   *
   * À l'arrêt, le cap GPS n'est pas fiable : on garde le cap du segment tel
   * quel plutôt que de le retourner sur du bruit.
   */
  #alignWithGps(segmentBearing: number): number {
    return this.#usingGpsBearing
      ? alignBearing(segmentBearing, this.#targetBearing)
      : segmentBearing
  }

  /**
   * Avance d'une image et renvoie l'état à afficher.
   *
   * @param dtSeconds Durée écoulée ; la valeur par défaut correspond à 60 Hz.
   */
  tick(dtSeconds = FRAME_SECONDS): NavigationState {
    if (!this.#hasFix) {
      return this.#state()
    }

    let effectiveLat = this.#targetLat
    let effectiveLon = this.#targetLon

    const moving = this.#speedMs > BEARING_SPEED_THRESHOLD_MS
    const info = this.#snapInfo

    if (info !== null && moving && this.#usingGpsBearing) {
      if (info.isTrackSnap && info.track !== null) {
        // Extrapolation curviligne : on suit la géométrie de la trace.
        this.#curvilinearDistance += this.#speedMs * dtSeconds

        const reckoned = positionBearingAtDistance(
          info.track,
          this.#curvilinearDistance,
        )

        if (reckoned !== null) {
          effectiveLat = reckoned.lat
          effectiveLon = reckoned.lon
          this.#curvilinearBearing = this.#alignWithGps(reckoned.bearing)
        }
      } else {
        // Extrapolation rectiligne le long du segment routier, plafonnée.
        this.#deadReckonMeters = Math.min(
          this.#deadReckonMeters + this.#speedMs * dtSeconds,
          this.#speedMs * MAX_DEAD_RECKON_SECONDS,
        )

        const projected = advance(
          info.lat,
          info.lon,
          this.#alignWithGps(info.segmentBearing),
          this.#deadReckonMeters,
        )
        effectiveLat = projected.lat
        effectiveLon = projected.lon
      }
    } else if (info !== null) {
      // Accroché, mais l'extrapolation n'est pas permise : trop lent, ou pas
      // de cap GPS. On se pose sur la position accrochée.
      //
      // Android n'attache **aucune** condition de vitesse à cette branche
      // (`MapManager.kt:654`) : elle rattrape tout ce que la première refuse.
      // Le portage ne rattrapait que « trop lent », si bien qu'accroché, en
      // mouvement et sans cap GPS — le premier relevé d'un départ —, la
      // position partait en extrapolation rectiligne depuis le fix brut, à un
      // cap périmé, au lieu de se poser sur la trace.
      effectiveLat = info.lat
      effectiveLon = info.lon
    } else if (moving && this.#usingGpsBearing) {
      this.#deadReckonMeters = Math.min(
        this.#deadReckonMeters + this.#speedMs * dtSeconds,
        this.#speedMs * MAX_DEAD_RECKON_SECONDS,
      )

      const projected = advance(
        this.#targetLat,
        this.#targetLon,
        this.#targetBearing,
        this.#deadReckonMeters,
      )
      effectiveLat = projected.lat
      effectiveLon = projected.lon
    }

    this.#currentLat += (effectiveLat - this.#currentLat) * POSITION_ALPHA
    this.#currentLon += (effectiveLon - this.#currentLon) * POSITION_ALPHA

    if (this.#usingGpsBearing) {
      const targetBearing =
        info !== null && moving ? this.#curvilinearBearing : this.#targetBearing

      const delta = normalizeBearingDelta(targetBearing - this.#currentBearing)
      this.#currentBearing =
        (this.#currentBearing + delta * BEARING_ALPHA + 360) % 360
    }

    // Convergence mesurée contre la position **effective** — accrochée le cas
    // échéant — et non contre le relevé brut : à l'arrêt sur une trace,
    // l'affichage converge vers le point accroché, qui peut rester à 25 m du
    // relevé. Comparer au relevé rendrait la veille inatteignable, et la
    // caméra tournerait à plein régime à l'arrêt.
    this.#converged =
      Math.abs(effectiveLat - this.#currentLat) < POSITION_CONVERGED_DEG &&
      Math.abs(effectiveLon - this.#currentLon) < POSITION_CONVERGED_DEG &&
      (!this.#usingGpsBearing ||
        Math.abs(
          normalizeBearingDelta(this.#targetBearing - this.#currentBearing),
        ) < BEARING_CONVERGED_DEG) &&
      this.#speedMs <= BEARING_SPEED_THRESHOLD_MS

    return this.#state()
  }

  #state(): NavigationState {
    return {
      lat: this.#currentLat,
      lon: this.#currentLon,
      bearing: this.#currentBearing,
      speedMs: this.#speedMs,
      snappedTrack: this.snappedTrack,
      curvilinearDistance: this.#curvilinearDistance,
      isTrackSnap: this.#snapInfo?.isTrackSnap ?? false,
      converged: this.#converged,
    }
  }
}

/**
 * Avance d'une distance donnée dans une direction donnée.
 *
 * Approximation plane, largement suffisante sur les quelques dizaines de
 * mètres que couvre une extrapolation.
 */
function advance(
  lat: number,
  lon: number,
  bearingDegrees: number,
  meters: number,
): { lat: number; lon: number } {
  const radians = (bearingDegrees * Math.PI) / 180
  const degrees = meters / METERS_PER_DEGREE_LAT

  const nextLat = lat + Math.cos(radians) * degrees
  const cosLat = Math.max(Math.cos((nextLat * Math.PI) / 180), 0.01)

  return {
    lat: nextLat,
    lon: lon + (Math.sin(radians) * degrees) / cosLat,
  }
}
